"use client";

import * as React from "react";
import { ChevronDown } from "lucide-react";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "../ui/Command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/Popover";
import { Checkbox } from "../ui/Checkbox";
import SearchIcon from "../../media/SearchIcon";
import { useVirtualizer } from "@tanstack/react-virtual";
import { TruncatedTooltipText } from "../map/TruncatedTooltipText";

export interface FilterOption {
    value: string;
    label: string;
    color?: string;
}

export interface QueueInsightsGlobalFilterProps {
    title: string;
    options: FilterOption[];
    selectedValues: string[];
    onChange: (values: string[]) => void;
    placeholder?: string;
    theme: "light" | "dark";
    loading?: boolean;
}

export function QueueInsightsGlobalFilter({
    title,
    options,
    selectedValues,
    onChange,
    placeholder = "Select options...",
    theme,
    loading = false,
}: QueueInsightsGlobalFilterProps) {
    
    const [listElement, setListElement] = React.useState<HTMLDivElement | null>(null);
    const [open, setOpen] = React.useState(false);

    const [searchQuery, setSearchQuery] = React.useState("");
    const filteredOptions = React.useMemo(() => {
        if (!searchQuery) return options;
        return options.filter(option => 
            option.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [options, searchQuery]);

    const rowVirtualizer = useVirtualizer({
        count: filteredOptions.length,
        getScrollElement: () => listElement,
        estimateSize: () => 35,
        overscan: 5,
    });

    const handleSelect = React.useCallback((value: string) => {
        const newSelectedValues = selectedValues.includes(value)
            ? selectedValues.filter((item) => item !== value)
            : [...selectedValues, value];

        onChange(newSelectedValues);
    }, [selectedValues, onChange]);

    const handleSelectAll = React.useCallback(() => {
        const allValues = options.map(option => option.value);
        onChange(allValues);
    }, [options, onChange]);

    const handleClearAll = React.useCallback(() => {
        onChange([]);
    }, [onChange]);

    const selectedLabels = React.useMemo(() => {
        if (loading) return "Loading...";
        if (selectedValues.length === 0) return 'None';
        if (selectedValues.length === 1) {
            return (
                options.find((option) => option.value === selectedValues[0])
                    ?.label || placeholder
            );
        }
        return `${selectedValues.length} selected`;
    }, [selectedValues, options, placeholder, loading]);

    return (
        <div className="flex flex-col w-full">
            <div className="flex flex-row items-center justify-between mb-2">
                <div className="text-[12px] font-semibold text-black dark:text-white">
                    {title}
                </div>
                <div className="flex flex-row gap-1">
                    <button
                        className="py-[2px] px-[4px] border-[1px] border-[#22C55E] dark:border-[#22C55E] text-[11px] rounded-[3px] focus-visible:outline-none hover:bg-[#22C55E] dark:hover:bg-[#22C55E] text-[#22C55E] dark:text-[#22C55E] hover:text-white dark:hover:text-white font-semibold duration-200"
                        onClick={handleSelectAll}
                        disabled={loading}
                        title="Select All"
                    >
                        Select All
                    </button>
                    <button
                        className="py-[2px] px-[4px] border-[1px] border-[#F62A2A] dark:border-[#F62A2A] text-[11px] rounded-[3px] focus-visible:outline-none hover:bg-[#F62A2A] dark:hover:bg-[#F62A2A] text-[#F62A2A] dark:text-[#F62A2A] hover:text-white dark:hover:text-[#2D3748] font-semibold duration-200"
                        onClick={handleClearAll}
                        disabled={loading}
                        title="Clear All"
                    >
                        Clear All
                    </button>
                </div>
            </div>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger 
                    className="py-[2.5px] px-[8px] flex gap-4 bg-white dark:bg-[#2D3748] text-sm rounded-[4px] items-center focus-visible:outline-none hover:bg-[#C6C5C5] dark:hover:bg-[#4A5568] duration-200 justify-between"
                    disabled={loading}
                >
                    <div className="text-black dark:text-[#CEE0EF] flex-1 min-w-0 text-left">
                        <TruncatedTooltipText text={selectedLabels} className="text-left" />
                    </div>
                    <ChevronDown className="h-4 w-4 stroke-[#D9D9D9] dark:stroke-[#7A8690]" />
                </PopoverTrigger>
                <PopoverContent
                    className="w-[250px] p-0 border border-gray-300 dark:border-0 bg-white dark:bg-[#2D3748] text-[#232B36] dark:text-[#CEE0EF] shadow-md"
                    align="start"
                >
                    <Command className="flex h-full w-full flex-col overflow-hidden scrollbar-hide rounded-md bg-white dark:bg-[#2D3748] text-popover-foreground">
                        <div className="relative flex flex-row w-full px-2 my-1">
                            <CommandInput
                                placeholder="Search..."
                                hideIcon
                                className="text-[#232B36] dark:text-[#CEE0EF] text-[13px] px-2 focus:outline-none dark:bg-[#2D3748] rounded-[3px] border-[1px] border-[#C6C5C5] dark:border-[#7A8690] mt-1 pl-8 w-full h-[30px]"
                                disabled={loading}
                                value={searchQuery}
                                onValueChange={setSearchQuery}
                            />
                            <div className="absolute left-[12px] top-[19px] transform -translate-y-1/2 pointer-events-none">
                                <SearchIcon theme={theme} />
                            </div>
                        </div>
                        <CommandList 
                            ref={setListElement}
                            className="max-h-[300px] overflow-y-auto overflow-x-hidden mb-1"
                        >
                            {loading ? (
                                <div className="flex items-center justify-center py-4 text-sm text-gray-500 dark:text-gray-400">
                                    Loading options...
                                </div>
                            ) : filteredOptions.length === 0 ? (
                                <CommandEmpty>No results found.</CommandEmpty>
                            ) : (
                                <CommandGroup className="overflow-hidden">
                                    <div 
                                        style={{ 
                                            height: `${rowVirtualizer.getTotalSize()}px`,
                                            width: '100%',
                                            position: 'relative'
                                        }}
                                    >
                                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                                            const option = filteredOptions[virtualRow.index];
                                            if (!option) return null;
                                            
                                            return (
                                                <div
                                                    key={`${option.value}-${virtualRow.index}`}
                                                    data-index={virtualRow.index}
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: `${virtualRow.size}px`,
                                                        transform: `translateY(${virtualRow.start}px)`
                                                    }}
                                                >
                                                    <CommandItem
                                                        value={option.value}
                                                        onSelect={() => handleSelect(option.value)}
                                                        className="relative flex cursor-default rounded-md font-semibold hover:cursor-pointer select-none items-center px-2 py-1.5 text-sm outline-none transition-colors hover:bg-[#F1F1F1] dark:hover:bg-[#4A5568]"
                                                    >
                                                        <div className="flex items-center gap-2 w-full">
                                                            <Checkbox
                                                                id={`filter-${option.value}-${virtualRow.index}`}
                                                                checked={selectedValues.includes(option.value)}
                                                                onCheckedChange={() => handleSelect(option.value)}
                                                                className="border-[#C6C5C5] data-[state=checked]:bg-accent1 data-[state=checked]:border-accent1 flex-shrink-0"
                                                            />
                                                            <label
                                                                htmlFor={`filter-${option.value}-${virtualRow.index}`}
                                                                className="text-[13px] text-[#232B36] dark:text-[#CEE0EF] cursor-pointer select-none flex-1 min-w-0"
                                                            >
                                                                <TruncatedTooltipText text={option.label} />
                                                            </label>
                                                        </div>
                                                    </CommandItem>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CommandGroup>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
}
